import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, AlertTriangle, RefreshCw, ExternalLink, HardDrive, Building2, Settings as SettingsIcon,
} from "lucide-react";

const STATUS_META = {
  ok: { color: "emerald", label: "OK", emoji: "✅" },
  missing_provision: { color: "amber", label: "Non provisionné", emoji: "⚠️" },
  missing_subfolders: { color: "amber", label: "Sous-dossiers manquants", emoji: "📂" },
  root_deleted: { color: "red", label: "Dossier supprimé", emoji: "❌" },
};

export default function SuppliersDriveHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remediating, setRemediating] = useState(null);
  const [showParentModal, setShowParentModal] = useState(false);
  const [parentInput, setParentInput] = useState("");
  const [savingParent, setSavingParent] = useState(false);
  const [parentInfo, setParentInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/suppliers/drive-health-check");
      setData(r.data);
      setShowParentModal(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "drive_parent_missing") {
        setShowParentModal(true);
        // Pre-fetch any prior value
        try {
          const p = await api.get("/admin/suppliers-drive-parent");
          setParentInfo(p.data);
        } catch (_) { /* noop */ }
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Échec du Health Check"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveParent = async () => {
    if (!parentInput.trim()) {
      toast.error("Veuillez coller l'URL ou l'ID du dossier Drive parent.");
      return;
    }
    setSavingParent(true);
    try {
      await api.post("/admin/suppliers-drive-parent", { parent_id_or_url: parentInput.trim() });
      toast.success("Dossier parent enregistré. Lancement du Health Check…");
      setParentInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingParent(false);
    }
  };

  const remediate = async (item) => {
    setRemediating(item.supplier_id);
    try {
      const r = await api.post(`/suppliers/${item.supplier_id}/drive-remediate`);
      const { bcs_uploaded, invoices_uploaded } = r.data.remediated;
      toast.success(`${item.name} provisionné. ${bcs_uploaded} BC + ${invoices_uploaded} factures uploadés.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Échec");
    } finally {
      setRemediating(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <HardDrive size={22} /> Fournisseurs Drive Health Check
        </h1>
        <p className="text-sm text-zinc-600 max-w-3xl leading-relaxed">
          Audite la structure Drive de chaque fournisseur (<code>{`{NNN}-{Nom}/001-BDC/`}</code> + <code>002-Factures reçues/</code>) et remédie les manquants. Les nouveaux fournisseurs sont automatiquement provisionnés.
        </p>
      </header>

      {data?.parent_id && (
        <div className="text-xs text-zinc-500 font-mono flex items-center gap-2">
          <SettingsIcon size={12} /> Dossier parent : <code className="text-zinc-700">{data.parent_id}</code>
          <button
            onClick={() => setShowParentModal(true)}
            data-testid="suppliers-drive-change-parent"
            className="text-blue-600 hover:underline"
          >
            modifier
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={load}
          disabled={loading}
          data-testid="suppliers-drive-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-xs font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Relancer
        </button>
        {data && (
          <div className="ml-auto text-xs text-zinc-500 font-mono">
            {data.scanned} fournisseurs · {data.totals?.pos_to_reupload || 0} BCs et {data.totals?.invoices_to_reupload || 0} factures à pousser
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.counters || {}).map(([k, v]) => {
            const meta = STATUS_META[k] || { color: "zinc", label: k };
            return (
              <div key={k} className={`p-3 rounded-md border border-zinc-100 bg-white`} data-testid={`suppliers-drive-counter-${k}`}>
                <div className={`text-[10px] font-mono uppercase tracking-wider text-${meta.color}-700`}>{meta.emoji} {meta.label}</div>
                <div className="text-xl font-light text-zinc-800 mt-1">{v}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600 font-mono uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Fournisseur</th>
              <th className="px-3 py-2 text-left">Dossier Drive</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-right">BCs / Factures à pousser</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(!data?.items || data.items.length === 0) && (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-zinc-500">
                {loading ? "Chargement…" : "Aucun fournisseur."}
              </td></tr>
            )}
            {data?.items?.map((it) => {
              const meta = STATUS_META[it.status] || { color: "zinc", label: it.status };
              return (
                <tr key={it.supplier_id} data-testid={`suppliers-drive-row-${it.supplier_id}`}>
                  <td className="px-3 py-2 font-medium text-zinc-800 flex items-center gap-1.5">
                    <Building2 size={11} className="text-zinc-400" />
                    {it.name}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-600">
                    {it.drive_root_name || "—"}
                    {it.drive_root_web_link && (
                      <a href={it.drive_root_web_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center ml-1 text-blue-600 hover:underline">
                        <ExternalLink size={9} />
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-${meta.color}-200 bg-${meta.color}-50 text-${meta.color}-700`}>
                      {meta.emoji} {meta.label}
                    </span>
                    {it.detail && <span className="ml-2 text-[10px] text-zinc-500">{it.detail}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">
                    {it.pos_missing_drive || 0} / {it.invoices_missing_drive || 0}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.remediation_available && (
                      <button
                        onClick={() => remediate(it)}
                        disabled={remediating === it.supplier_id}
                        data-testid={`suppliers-drive-remediate-${it.supplier_id}`}
                        className="inline-flex items-center gap-1 px-2 h-7 border border-blue-200 bg-blue-50 text-blue-700 rounded text-[11px] font-mono uppercase hover:bg-blue-100 disabled:opacity-50"
                      >
                        {remediating === it.supplier_id ? <Loader2 size={10} className="animate-spin" /> : <HardDrive size={10} />} Remédier
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* First-run / change-parent modal */}
      {showParentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="suppliers-drive-parent-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <SettingsIcon size={18} /> Dossier Drive parent des fournisseurs
            </h2>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Collez l'URL Google Drive du dossier parent (ou son ID brut). Chaque nouveau fournisseur sera créé en sous-dossier <code>{`{NNN}-{Nom}`}</code> avec <code>001-BDC/</code> et <code>002-Factures reçues/</code>.
            </p>
            {parentInfo?.parent_id && (
              <div className="bg-zinc-50 rounded p-2 text-[11px] font-mono text-zinc-600">
                Actuel : {parentInfo.parent_id}
              </div>
            )}
            <input
              type="text"
              value={parentInput}
              onChange={(e) => setParentInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              data-testid="suppliers-drive-parent-input"
              className="w-full border border-zinc-200 rounded px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              {data?.parent_id && (
                <button onClick={() => setShowParentModal(false)} className="px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900">
                  Annuler
                </button>
              )}
              <button
                onClick={saveParent}
                disabled={savingParent}
                data-testid="suppliers-drive-parent-save"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50"
              >
                {savingParent ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
